import { Navigate } from 'react-router-dom'
import { usePermissions } from '@/modules/auth/hooks/usePermissions'
import type { PermissionKey } from '@/modules/auth/permissions/permissions'

interface PermissionRouteProps {
  /** Permission required to access this route */
  permission: PermissionKey
  children: React.ReactNode
}

/**
 * Restricts access to routes that require a specific permission.
 * If the user lacks the permission, redirects to /forbidden.
 * Must be nested inside ProtectedRoute (assumes authentication is already checked).
 *
 * @example
 * ```tsx
 * <Route
 *   path="/users"
 *   element={
 *     <PermissionRoute permission={Permission.USERS_VIEW}>
 *       <UsersListPage />
 *     </PermissionRoute>
 *   }
 * />
 * ```
 */
export function PermissionRoute({ permission, children }: PermissionRouteProps) {
  const { hasPermission } = usePermissions()

  if (!hasPermission(permission)) {
    return <Navigate to="/forbidden" replace />
  }

  return <>{children}</>
}
